import { createSSRApp } from 'vue'
import type { ComponentPublicInstance } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import { createRouter } from './router'
import SVGIconNext from '@/components/svg-icon-next/index.vue'
// bili-design 按需引入（通过 unplugin-vue-components 自动导入组件和样式）
// 只需引入基础样式
import '@bilibili-business/bili-design/theme-chalk/base.css'
import svgIcon from '@bilibili-business/svg-icon-vue'
import '@bilibili-business/svg-icon-vue/style.css'
import './style/reset.less'

// SSR 组件渲染耗时统计
const isServer = typeof window === 'undefined'
const componentTimings: Map<string, number> = new Map()
const componentStartTimes: Map<string, number> = new Map()

const ssrLog = (msg: string) => {
  if (isServer) {
    console.log(`[SSR Component] ${msg}`)
  }
}

// 根据环境变量获取静态资源 CDN 域名
// SSR 时从 globalThis.__ENV_CONFIG__ 读取，客户端从 window.__ENV_CONFIG__ 读取
/* eslint-disable no-undef */
const getStaticHost = () => {
  const envConfig = isServer
    ? (globalThis as any).__ENV_CONFIG__
    : (window as any).__ENV_CONFIG__
  const env = envConfig?.STATIC_ENV || ''
  return env && env !== 'prod' ? `${env}-s1.hdslb.com` : 's1.hdslb.com'
}
/* eslint-enable no-undef */

export function createApp() {
  const app = createSSRApp(App)
  const router = createRouter()
  const pinia = createPinia()

  // SSR 组件渲染耗时 mixin
  if (isServer) {
    app.mixin({
      beforeCreate() {
        const name = getComponentName(this)
        if (name && shouldTrackComponent(name)) {
          componentStartTimes.set(name, Date.now())
        }
      },
      created() {
        const name = getComponentName(this)
        if (name && shouldTrackComponent(name)) {
          const startTime = componentStartTimes.get(name)
          if (startTime) {
            const elapsed = Date.now() - startTime
            const prev = componentTimings.get(name) || 0
            componentTimings.set(name, prev + elapsed)
          }
        }
      },
      serverPrefetch() {
        const name = getComponentName(this)
        if (name) {
          ssrLog(`serverPrefetch start: ${name}`)
        }
        return Promise.resolve().then(() => {
          if (name) {
            ssrLog(`serverPrefetch end: ${name}`)
          }
        })
      }
    })
  }

  app.use(pinia)
  app.use(router)
  // bili-design 组件由 unplugin-vue-components 自动注册
  app.use(svgIcon, {
    baseUrl: `https://${getStaticHost()}/bfs/static/ad-fly-landing/`
  })
  app.component('SVGIconNext', SVGIconNext)

  return {
    app,
    router,
    pinia,
    getComponentTimings: () => {
      const sorted = [...componentTimings.entries()].sort((a, b) => b[1] - a[1])
      return sorted
    },
    printComponentTimings: () => {
      const sorted = [...componentTimings.entries()].sort((a, b) => b[1] - a[1])
      ssrLog('=== Component Timings ===')
      sorted.forEach(([name, time]) => {
        ssrLog(`  ${name}: ${time}ms`)
      })
      ssrLog('========================')
    }
  }
}

// 获取组件名称
function getComponentName(instance: ComponentPublicInstance): string {
  return (
    instance.$options.name ||
    instance.$options.__name ||
    instance.$options.__file?.split('/').pop()?.replace('.vue', '') ||
    ''
  )
}

// 只追踪自定义组件，过滤掉第三方库组件
function shouldTrackComponent(name: string): boolean {
  // 过滤掉 bili-design 和其他库的组件
  if (name.startsWith('Bd') || name.startsWith('bd-')) return false
  if (name.startsWith('El') || name.startsWith('el-')) return false
  if (name.startsWith('SVG')) return false
  if (!name || name === 'Anonymous') return false
  return true
}
